import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { logRequest, logResponse, logError } from '@/utils'; // Import logging utilities

// Set global defaults
axios.defaults.withCredentials = true; // Include credentials (cookies) in requests
axios.defaults.withXSRFToken = true;

const baseURL =
  import.meta.env.VITE_ENV === 'development'
    ? import.meta.env.VITE_LOCAL_API_URL // Use local API URL in development
    : import.meta.env.VITE_API_URL; // Use production API URL in production

// Check if DEBUG_LOG is enabled
const isDebugLogEnabled = import.meta.env.VITE_DEBUG_LOG === 'true';

// Create a single Axios instance
const api = axios.create({
  baseURL: baseURL, // Use environment variable for base URL
  headers: {
    'X-Requested-With': 'XMLHttpRequest', // Required for Laravel Sanctum
  },
});

// Add request interceptor
api.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    // Skip interceptor for requests with the custom header
    if (config.headers?.['X-Skip-Interceptor']) {
      return config;
    }

    const accessToken = localStorage.getItem('accessToken');
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Log request only if DEBUG_LOG is enabled
    if (isDebugLogEnabled) {
      logRequest(config);
    }

    return config;
  },
  (error: AxiosError) => {
    // Log error only if DEBUG_LOG is enabled
    if (isDebugLogEnabled) {
      logError(error);
    }
    return Promise.reject(error);
  }
);

// Add response interceptor
api.interceptors.response.use(
  (response) => {
    // Log response only if DEBUG_LOG is enabled
    if (isDebugLogEnabled) {
      logResponse(response);
    }
    return response;
  },
  (error) => {
    // Log error only if DEBUG_LOG is enabled
    if (isDebugLogEnabled) {
      logError(error);
    }
    return Promise.reject(error);
  }
);

// Fetch CSRF token
export const fetchCsrfToken = async () => {
  try {
    await api.get('/sanctum/csrf-cookie', {
      headers: {
        'X-Requested-With': 'XMLHttpRequest', // Required for Laravel Sanctum
      },
    });
  } catch (error: any) {
    console.error('Failed to fetch CSRF token:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

// Helper to handle errors from API responses
export const handleApiError = (error: any): never => {
  // Extract error message from response
  let errorMessage = 'An unexpected error occurred';

  if (error.response?.data) {
    const { message, errors } = error.response.data;

    if (message) {
      errorMessage = message;
    } else if (errors) {
      // Handle case where errors is an array
      if (Array.isArray(errors)) {
        errorMessage = errors.join(', ');
      }
      // Handle case where errors is an object (field-based validation errors)
      else if (typeof errors === 'object' && errors !== null) {
        errorMessage = Object.entries(errors)
          .map(([field, messages]) => {
            if (Array.isArray(messages)) {
              return `${field}: ${messages.join(', ')}`;
            }
            return `${field}: ${messages}`;
          })
          .join('; ');
      }
      // Handle case where errors is a string
      else if (typeof errors === 'string') {
        errorMessage = errors;
      }
    }
  } else if (error.message) {
    errorMessage = error.message;
  }

  throw new Error(errorMessage);
};

export default api;